import {
  HttpRequest,
  HttpResponse,
  HttpResponseMapper,
  ObjectInterceptor,
  HttpRequestInterceptor
} from './interfaces';
import composeResponseMappers from './composeResponseMappers';
import logger from '../log';
import InterceptorType from './InterceptorType';
import copyRequest from './copyRequest';
import { OidFunction } from '@kollective-frontend/ksdk-api';
import { InterceptorDisabled } from './errors';
import { dispatchInterceptorChangeEvent } from '../events/dispatch';
import ContentConfig from '../config/ContentConfig';
import EventEmitter from '../common/EventEmitter';
import { InternalEvents } from '../events';
import { ObjectIdFunction, getObjectIdFunction } from './getObjectIdFunction';
const log = logger.getLogger(__filename);

export default class InterceptorStack implements HttpRequestInterceptor {
  stack: ObjectInterceptor[];
  responseMapper: HttpResponseMapper | undefined;
  interceptorType = InterceptorType.COMPOSITE;
  createObjectId?: OidFunction;
  config: ContentConfig;
  shouldReport: boolean;
  eventEmitter: EventEmitter;
  objectIdFunction: ObjectIdFunction;

  constructor(
    config: ContentConfig,
    shouldReport?: boolean,
    createObjectId?: OidFunction
  ) {
    this.stack = [];
    this.config = config;
    this.shouldReport = shouldReport || false;
    this.eventEmitter = EventEmitter.getInstance();
    this.objectIdFunction = getObjectIdFunction(
      this.config.app as string,
      createObjectId
    );
  }

  currentPeerConnections(): number {
    return this.stack.reduce((sum, current) => {
      if (typeof current.currentPeerConnections === 'function') {
        return sum + current.currentPeerConnections();
      }
      return sum;
    }, 0);
  }

  putInterceptor(interceptor: ObjectInterceptor): void {
    // unshift puts the value on the front of the array.
    this.stack.unshift(interceptor);
    this.signalInterceptorChange(interceptor, true);
  }

  removeInterceptor(interceptor: ObjectInterceptor): void {
    this.stack = this.stack.filter(i => i !== interceptor);
    this.signalInterceptorChange(interceptor, false);
  }

  signalInterceptorChange(interceptor: ObjectInterceptor, add: boolean): void {
    if (this.shouldReport && interceptor.interceptorName) {
      const name = interceptor.interceptorName;
      dispatchInterceptorChangeEvent({ add, name });
    }
  }

  registerResponseMapper(mapper: HttpResponseMapper): void {
    if (this.responseMapper) {
      this.responseMapper = composeResponseMappers(this.responseMapper, mapper);
    } else {
      this.responseMapper = mapper;
    }
  }

  async handle(request: HttpRequest): Promise<HttpResponse> {
    const toRemove: ObjectInterceptor[] = [];
    const { url, headers } = request;
    const objectId = this.objectIdFunction(url, { headers });
    const req = {
      ...request,
      objectId
    };
    this.eventEmitter.emit(InternalEvents.PENDING_OBJECT, {
      data: { objectId }
    });
    for (const interceptor of this.stack) {
      try {
        // Return the first available response
        const resp = await interceptor.handle(copyRequest(req));
        log.trace('Handled by', interceptor.interceptorName);
        if (this.responseMapper) {
          return Promise.resolve(this.responseMapper(resp));
        } else {
          return Promise.resolve(resp);
        }
      } catch (e) {
        const name = interceptor.interceptorName;
        log.trace('Unable to get a response with', name, e);
        if (e && (e as InterceptorDisabled).interceptorDisabled) {
          toRemove.push(interceptor);
        }
      }
    }
    log.debug(
      'All interceptors failed, removing the following interceptors:',
      toRemove
    );
    // if any interceptors are disabled, remove them.
    toRemove.forEach(i => this.removeInterceptor(i));
    // If we make it here, then it's bad.
    return Promise.reject({
      status: 0,
      request,
      url: request.url
    });
  }
}
